import React, { useEffect, useMemo, useState } from 'react';
import useIsMountedRef from 'utils/hooks/useIsMountedRef';
import DropdownTreeSelect, { Mode, TreeNode, TextProps } from 'react-dropdown-tree-select';
import BrukereApi, { IBruker } from 'services/KjederegisteretAdminApi/BrukereApi';
import CommonLoader from 'components/CommonLoader';

interface Props {
  treeSelectMode?: Mode;
  customTexts?: TextProps;
  disabled?: boolean;
  onSelectionChanged: (brukere: IMappedBruker[]) => void;
  findPreselectedInFilter?: (picker: string) => string[];
  /**
   * Antall treff (fasett) pr. bruker-id. Når satt vises antallet som suffiks på
   * hver bruker i lista. Spesialradene "Meg"/"Noen" har ikke fasett-tall.
   */
  facetCounts?: Map<string, number>;
  /** Når satt (sammen med facetCounts) skjules uvalgte brukere med 0 treff. */
  hideEmptyFacets?: boolean;
}
export interface IMappedBruker {
  id: number;
  brukerId: string;
  label: string;
  checked?: boolean;
}

const BrukerPicker: React.FC<Props> = (props) => {
  const [rawBrukere, setRawBrukere] = useState<IBruker[]>();
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const isMountedRef = useIsMountedRef();

  const defMode = 'radioSelect';
  const defTexts = {
    placeholder: 'Velg...',
    noMatches: 'Ingen treff',
  };

  let pickedusers: string[] = [];
  if (props.findPreselectedInFilter) {
    pickedusers = props.findPreselectedInFilter('bruker');
  }

  const mapBrukerNode = (node: TreeNode) => {
    const item: IMappedBruker = {
      id: node.id,
      label: node.label,
      brukerId: node.brukerId,
      checked: pickedusers && pickedusers.includes(node.brukerId) ? true : false,
    };
    return item;
  };

  const specialRows = ['Meg', 'Noen'];

  const mapTreeItem = (node: IBruker) => {
    const isChecked = pickedusers && pickedusers.includes(node.brukerID) ? true : false;
    const baseLabel = node.fornavn + ' ' + (node.etternavn ? node.etternavn : '');
    // Spesialradene "Meg"/"Noen" er syntetiske og har ikke fasett-tall.
    const label =
      props.facetCounts && !specialRows.includes(node.brukerID.toString())
        ? `${baseLabel} (${(props.facetCounts.get(node.brukerID.toString()) ?? 0).toLocaleString('nb-NO')})`
        : baseLabel;
    const item: IMappedBruker = {
      id: parseInt(node.brukerID),
      label,
      brukerId: node.brukerID.toString(),
      checked: isChecked,
    };
    return item;
  };

  const loadBrukere = async () => {
    setIsFetching(true);
    let res = await BrukereApi.getBrukere();
    let extraRows: string[] = ['Meg', 'Noen'];
    if (extraRows?.length > 0) {
      extraRows.map((x) => res.unshift({ id: Number(x), brukerID: x, fornavn: x }));
    }
    if (res.length > 0 && isMountedRef.current) {
      setRawBrukere(res);
    }
    setIsFetching(false);
  };

  // Re-mappes når fasett-tallene endrer seg (ny referanse pr. søk) slik at
  // labels oppdateres uten å hente brukerlista på nytt.
  const data = useMemo(() => {
    const mapped = rawBrukere?.map((x) => mapTreeItem(x));
    if (!mapped || !props.facetCounts || !props.hideEmptyFacets) return mapped;
    // Skjul brukere uten treff (fasett-count 0), men behold spesialradene og valgte brukere.
    return mapped.filter(
      (node) =>
        specialRows.includes(node.brukerId) ||
        node.checked ||
        (props.facetCounts.get(node.brukerId) ?? 0) > 0,
    );
  }, [rawBrukere, props.facetCounts, props.hideEmptyFacets]);

  const handleChange = (currentNode: TreeNode, selectedNodes: TreeNode[]) => {
    if (Array.isArray(selectedNodes)) {
      props.onSelectionChanged(selectedNodes.map((x) => mapBrukerNode(x)));
    }
  };

  useEffect(() => {
    if (!rawBrukere) {
      loadBrukere();
    }
  }, [rawBrukere]);

  return (
    <>
      <DropdownTreeSelect
        className="reactDropdownMultiPicker"
        mode={props.treeSelectMode || defMode}
        texts={props.customTexts || defTexts}
        data={data || []}
        keepTreeOnSearch
        keepChildrenOnSearch
        showPartiallySelected
        onChange={(currentNode: TreeNode, selectedNodes: TreeNode[]) =>
          handleChange(currentNode, selectedNodes)
        }
        disabled={props.disabled || false}
      />
      {isFetching && (
        <div>
          <CommonLoader />
          <span> Henter data... </span>
        </div>
      )}
    </>
  );
};

export default React.memo(BrukerPicker);
